/**
 * `schemas/pads.ts` — the PADS bank: sixteen one-shot pads fired over the playing track.
 *
 * **There is no `spec.md` § behind this file, and that is a live problem, not an
 * oversight.** §6 describes forty-seven endpoints and none of them is `/api/pads/*`;
 * `tools/check-spec-coverage.mjs` diffs the `` `METHOD /path` `` headings of §6.3–§6.11
 * against the routes `apps/api` actually registers, **in both directions**, so the gate
 * goes red the moment the three routes below are wired. §9.2 ("the nav strip has three
 * items and there will not be a fourth"), §9.6 and §13.10's route table say the same
 * thing from the other side. A person has to edit those four places; nobody can make
 * this file legal by editing this file. The decisions this module encodes are recorded
 * in `wiki/pages/pads-screen-preflight.md` and
 * `wiki/pages/where-the-samples-come-from.md`, and each one is cited where it bites.
 *
 * Handle: `npm run check:spec-coverage` — expect it to name the pads routes as
 * "registered but not in the spec" until §6 gains them.
 *
 * ── What a pad actually is ──────────────────────────────────────────────────────
 *
 * A pad is not a track. It carries no `trackId`, no plate and no cover, because the
 * bank plays on its **own** `AudioContext` which never meets the single
 * `HTMLAudioElement` in `apps/web/src/engine/element.ts` — the two simply sound at the
 * same time. That is why a pad's audio is addressed by filename (see
 * {@link PAD_MEDIA_PREFIX}) and never through `/api/stream/:trackId`: no session, no
 * media token, no range logic, and no reason for the shapes below to know that a
 * catalogue exists.
 *
 * ── The three conventions of `schemas/*` still hold ─────────────────────────────
 *
 * §6.1's rules are followed exactly as `common.ts` states them: the zod schema is the
 * source of truth and every type is `z.infer` of one (the two exceptions are named and
 * type-tested), request bodies and responses are `z.strictObject`, and the Fastify JSON
 * Schema half is generated from the same declaration via `toFastifyRouteSchema()` so the
 * two validators cannot drift.
 *
 * ── Two traps that decide the shapes below ──────────────────────────────────────
 *
 *  1. **A slot value is a plain string, not a tagged union.** It is one `TEXT` column in
 *     one row, and — for a library sound — the stem of a filename this app concatenates
 *     into a URL. A `{ kind, id }` object would be three lines of marshalling in every
 *     one of the three processes for no gained safety, because the namespace is already
 *     unambiguous: `synth:` contains a colon and `[a-z0-9-]{1,48}` cannot, so a library
 *     id can never be mistaken for a voice or the reverse.
 *  2. **A missing sound is silent, not an error.** A pad whose assignment names a file
 *     that is not there produces no sound and no message: on a phone that is
 *     indistinguishable from a pad that is simply broken, and it is the one failure mode
 *     the operator cannot debug. Hence the validation here is deliberately paranoid at
 *     both ends — an unknown synth voice is rejected even though the wire regex would
 *     pass it, and a library sound's `file` must match its own `id`.
 */
import * as z from 'zod';
import { NonEmptyText, UnixSeconds } from './common.js';
/* ────────────────────────────────────────────────────────────────────────────
 * The library catalogue — GET /api/pads/sounds
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * The six groups the library list is sectioned by, **in the order it renders them** —
 * not alphabetical, and not the order of the bank (see {@link FACTORY_KIT}, whose rows
 * are laid out by thumb reach). `horns` leads because the air horn is the sound this
 * screen exists for, and the two smallest groups trail.
 *
 * `calls` is the human-made group — whistle, air-horn shout — and is deliberately
 * separate from `horns` (an instrument) and from `fx` (a machine): the three read as one
 * category only to somebody who has never had to find a sound in a list of sixty.
 */
export const PAD_CATEGORIES = ['horns', 'drums', 'keys', 'fx', 'bass', 'calls'];
export const PadCategory = z.enum(PAD_CATEGORIES);
/**
 * A library sound's id, which is **also the stem of its filename** — see
 * {@link PadSoundSchema}'s `file`.
 *
 * The alphabet is closed at `[a-z0-9-]` for two reasons, both of which have already cost
 * this project time:
 *
 *  - the id ends up inside `` `${PAD_MEDIA_PREFIX}${file}` ``, and `.` and `/` are the
 *    entire alphabet of a traversal — banning them here means no route below has to
 *    re-check it;
 *  - **lower case only, because the two filesystems this repo runs on disagree about
 *    case.** APFS on the dev machine is case-*insensitive*, the container's ext4 is
 *    case-sensitive, so `Hat-Closed.m4a` and `hat-closed.m4a` are one file locally and
 *    two files in production. The same class of bug as the trailing space in this
 *    repository's own directory name: it works on exactly one machine.
 */
export const PadSoundId = z.string().regex(/^[a-z0-9-]{1,48}$/, {
    error: 'padSoundId must be 1-48 characters of a-z, 0-9 and -',
});
/**
 * The silkscreen print on the pad face: `HAT 01`, `ONE DROP`, `HORN`.
 *
 * English, upper case, at most nine characters — the operator's decision, recorded in
 * `wiki/pages/pads-screen-preflight.md`: "No Russian on the pad faces. English panel
 * print only." That is not a stylistic preference, it is how sixteen Latin legends get
 * taught without a single tracked uppercase Cyrillic cap, which
 * `design-direction.md` §12.16 forbids outright. The Russian name of the sound lives in
 * `ru` and is printed in one lane under the plate instead.
 *
 * Nine characters is what fits a pad face at `--fs-11` without tracking-out; the regex
 * also refuses a leading or trailing space, which is invisible in a JSON file and shifts
 * the print off the pad's optical centre.
 *
 * `-` and `#` are in the alphabet because real panel print uses them and the library
 * already needs both: `X-STICK` is the cross-stick — the single most reggae sound on the
 * plate — and `SNAP A#` names a note. Both were rejected by the first version of this
 * regex and the rejection only surfaced in a production log, which is why the alphabet
 * is stated here rather than assumed.
 */
export const PadLabel = z.string().regex(/^[A-Z0-9](?:[A-Z0-9 #-]{0,7}[A-Z0-9#])?$/, {
    error: 'padLabel must be 1-9 characters of A-Z, 0-9, space, - and #, not starting or ending in a space',
});
/**
 * The licence every sound in the library carries. A closed set of exactly one member.
 *
 * **CC0 only, never CC BY** (`wiki/pages/where-the-samples-come-from.md`). A single CC BY
 * file obliges a permanent credits screen in the app, and the trap is live rather than
 * theoretical: the same Freesound uploader's 2014 sounds are CC0 while his 2015 sounds
 * are CC BY, and the site's licence facet counts them together — so the check has to
 * happen per sound, and this is where.
 */
export const PAD_LICENCE = 'CC0-1.0';
/**
 * File extensions a pad sound may ship as. A subset of `constants.ts`'s `EXT_TO_MIME`
 * keys, so `apps/api` maps the `Content-Type` from the table it already has.
 *
 * **`mp3` is absent on purpose.** Every MP3 encoder pads the head of the file with about
 * 26 ms of silence, which on a percussive one-shot is an audible late tap on every
 * single press — the one latency this screen cannot spend
 * (`wiki/pages/where-the-samples-come-from.md`). `mp4`, `oga` and `opus` are absent only
 * because nothing in the library needs them; adding one is a one-line edit here and
 * nothing anywhere else.
 */
export const PAD_AUDIO_EXTENSIONS = ['m4a', 'wav', 'ogg', 'aac', 'flac'];
/** `hat-closed-roots.m4a` — {@link PadSoundId} plus one {@link PAD_AUDIO_EXTENSIONS}. */
export const PadSoundFile = z.string().regex(/^[a-z0-9-]{1,48}\.(?:m4a|wav|ogg|aac|flac)$/, {
    error: 'padSoundFile must be <id>.<m4a|wav|ogg|aac|flac>',
});
/**
 * One sound in the server-side library, as `GET /api/pads/sounds` returns it.
 *
 * The last four fields are the licence audit and they are part of the wire shape rather
 * than a file nobody reads: `licences.json` beside the audio is what makes a CC0 claim
 * re-checkable a year from now, and a claim that only exists in a commit message is a
 * claim that will be re-derived by guessing. `author` is recorded even though CC0
 * requires no attribution — it is how a sound gets re-found, not a legal obligation.
 *
 * Two object-level refinements carry invariants the field regexes cannot see:
 *
 *  - **`file` must be `id` plus an extension.** The id *is* the stem, so a row whose
 *    file was renamed without its id is a 404 on a real pad press, and the pad goes
 *    quiet with no error anywhere (trap 2 in the module docblock).
 *  - **`licence` must be exactly {@link PAD_LICENCE}.** The type stays `string` because
 *    that is what the shared contract publishes; the value set does not.
 */
export const PadSoundSchema = z
    .strictObject({
    id: PadSoundId,
    /** The panel print. English, upper case — never the Russian name. */
    label: PadLabel,
    /** The Russian gloss the library list shows, sentence case (`Хэт закрытый`). */
    ru: NonEmptyText,
    category: PadCategory,
    file: PadSoundFile,
    /**
     * Seconds, **fractional** — unlike `TrackSummary.durationS`, which is `z.int()`.
     * A closed hat is 0.08 s and a rim shot is shorter; rounded to a whole second every
     * percussion sound in the library would be either 0 or 1.
     */
    durationS: z.number().positive(),
    /**
     * A plain integer, unlike `tracks.size_bytes` — which is a `TlLongString` because a
     * Telegram audio document exceeds 2^53. A one-shot is kilobytes; carrying it as a
     * decimal string here would be cargo cult.
     */
    bytes: z.int().positive(),
    /** Always `CC0-1.0`. See {@link PAD_LICENCE} for why this is not negotiable. */
    licence: NonEmptyText,
    author: NonEmptyText,
    /** The page the licence was read on — the individual sound's page, never the site's. */
    page: z.url(),
    /**
     * The date `tools/fetch-pad-sounds.mjs` read the licence off that page, `YYYY-MM-DD`.
     *
     * Optional because a sound installed by hand has no fetch date, and a missing date is
     * a fact rather than a default — `wiki/pages/where-the-samples-come-from.md` requires
     * the provenance of every sound to be readable off the product, and an invented date
     * would be worse than none. The build diagnostics the generator also knows (how much
     * silence it trimmed, whether it truncated a tail) deliberately do NOT appear here:
     * they belong in its log, not in a payload every client downloads.
     */
    licenceCheckedAt: z.iso.date().optional(),
})
    .refine((sound) => sound.file.startsWith(`${sound.id}.`), {
    error: 'file must be the sound id plus an extension',
    path: ['file'],
})
    .refine((sound) => sound.licence === PAD_LICENCE, {
    error: `licence must be ${PAD_LICENCE} — one CC BY file obliges a credits screen`,
    path: ['licence'],
});
/**
 * `GET /api/pads/sounds` — the whole library in one payload.
 *
 * Deliberately **not** a `Page<PadSound>`. The list is a few dozen rows of metadata
 * authored on the server, the picker groups it by {@link PAD_CATEGORIES} offline, and a
 * cursor would buy a second round trip in exchange for nothing.
 */
export const PadSoundsResponseSchema = z.strictObject({
    sounds: z.array(PadSoundSchema),
});
/**
 * Where the audio is served from, statically. Join it with a sound's `file`, which is
 * why `file` carries the extension and `id` does not:
 * `` `${PAD_MEDIA_PREFIX}${sound.file}` `` → `/media/pads/hat-closed-roots.m4a`.
 *
 * It lives in the shared package because two processes need the same string: `apps/api`
 * mounts it and `apps/web` builds URLs against it. Spelled twice it becomes wrong once.
 */
export const PAD_MEDIA_PREFIX = '/media/pads/';
/* ────────────────────────────────────────────────────────────────────────────
 * The bank — sixteen slots
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * The sixteen slots of the 4 × 4 bank, **in reading order**: `A01`–`A04` is the top row,
 * `A13`–`A16` the bottom one.
 *
 * Spelled out rather than generated. `Array.from({length: 16}, …)` would infer
 * `string[]`, `z.enum` would have nothing to enumerate and {@link PadSlot} would widen to
 * `string` — at which point every check in this file quietly stops checking. The
 * zero-padded two-digit form is what keeps `A02` and `A16` the same width in a
 * fixed-pitch panel print, and it sorts correctly as a string, which is what the DB
 * column will do.
 *
 * `A` is a bank letter with no second bank behind it yet — the MPC convention the
 * operator asked for names banks A–D, and this is bank A.
 */
export const PAD_SLOTS = [
    'A01',
    'A02',
    'A03',
    'A04',
    'A05',
    'A06',
    'A07',
    'A08',
    'A09',
    'A10',
    'A11',
    'A12',
    'A13',
    'A14',
    'A15',
    'A16',
];
/** One slot of the bank. Identical to `(typeof PAD_SLOTS)[number]`; type-tested as such. */
export const PadSlot = z.enum(PAD_SLOTS);
/** How many pads there are. Derived, so it cannot disagree with {@link PAD_SLOTS}. */
export const PAD_SLOT_COUNT = PAD_SLOTS.length;
/**
 * The seven drum-machine voices the app synthesises, referenced as `synth:<voice>`.
 *
 * **These are not placeholders and they are not a shortcut.** A dancehall riddim is
 * built on a drum machine; a "TR-808 cowbell sample" in any commercial pack is somebody's
 * recording of the same expired-patent circuit, and the spread between two such
 * recordings is wider than the spread between a recording and a good model. A live cowbell
 * on these pads would read as a genre error
 * (`wiki/pages/where-the-samples-come-from.md`). The four sounds synthesis genuinely
 * cannot fake — horn, whistle, melodica, rim — are the ones that arrive as files.
 *
 * The press-and-hold dub siren is not in this list: it is a sustained voice with its own
 * control, not a pad, and nothing in the sixteen slots can hold it.
 */
export const SYNTH_VOICES = [
    'shot',
    'one-drop',
    'snare',
    'tom',
    'cowbell',
    'bass',
    'brake',
];
export const SynthVoice = z.enum(SYNTH_VOICES);
/** The namespace marker that separates a synthesised voice from a library sound id. */
export const SYNTH_PREFIX = 'synth:';
/**
 * The wire pattern of a non-empty slot value: `synth:<voice>` or a library sound id.
 *
 * This regex — and only this regex — is what reaches ajv, because a zod refinement
 * cannot be represented in JSON Schema and `toFastifySchema()` drops it. That makes the
 * two validators §6.1 requires deliberately **coarse then fine**: ajv rejects the shape,
 * zod rejects the meaning (see {@link PadAssignmentSchema}). Keep it wide enough that
 * ajv never rejects something zod would have accepted, or a legal body starts failing
 * with ajv's error message instead of ours.
 */
export const PAD_ASSIGNMENT_PATTERN = /^(?:synth:[a-z-]{1,24}|[a-z0-9-]{1,48})$/;
/**
 * What one slot holds: a library sound id, a `synth:<voice>` reference, or `null` for an
 * empty pad.
 *
 * The `SYNTH_VOICES` membership check is the "fine" half described above, and it exists
 * because of trap 2 in the module docblock: `synth:airhorn` matches the pattern, has no
 * voice behind it, and produces a pad that is silent rather than broken. Rejecting it at
 * the boundary is the only place the mistake is still visible.
 *
 * `null` is a first-class value, not a missing one. Nothing ships empty — all sixteen of
 * {@link FACTORY_KIT}'s slots are playable — but a user may clear a pad, and they do it
 * by sending `null`, never by omitting the key, which {@link PadKitPutRequestSchema}
 * rejects.
 */
export const PadAssignmentSchema = z
    .string()
    .regex(PAD_ASSIGNMENT_PATTERN, {
    error: 'a pad assignment must be a sound id or synth:<voice>',
})
    .refine((value) => !value.startsWith(SYNTH_PREFIX) ||
    SYNTH_VOICES.includes(value.slice(SYNTH_PREFIX.length)), { error: 'unknown synth voice' })
    .nullable();
/**
 * All sixteen assignments as one object.
 *
 * `z.record` over an **enum** key is exhaustive in zod 4: every member is required and
 * any other key is an `unrecognized_keys` failure. That is the whole reason the map is
 * keyed by {@link PadSlot} rather than by `z.string()` — a partial kit cannot be
 * expressed, so no consumer has to decide what a missing `A07` means. Asserted in
 * `pads.test.ts` in both directions, because it is a library behaviour rather than
 * something written here.
 */
export const PadSlotMapSchema = z.record(PadSlot, PadAssignmentSchema);
/** One user's saved bank, as `GET`/`PUT /api/pads/kit` return it. */
export const PadKitSchema = z.strictObject({
    slots: PadSlotMapSchema,
    /** Unix seconds. `0` means "never saved" — see {@link FACTORY_KIT}. */
    updatedAt: UnixSeconds,
});
/**
 * `PUT /api/pads/kit` — a **full replace** of all sixteen slots.
 *
 * There is no partial update and there should not be one. The bank is what the user sees
 * in one glance; a per-slot `PATCH` would put sixteen round trips behind a drag-and-drop
 * gesture and would need a conflict rule for two tabs, which a whole-object write gets
 * for free. `updatedAt` is the server's to stamp, exactly as in `PlaybackPutRequest`.
 */
export const PadKitPutRequestSchema = z.strictObject({
    slots: PadSlotMapSchema,
});
/**
 * The response of both `GET` and `PUT /api/pads/kit`: the stored kit, with the server's
 * `updatedAt`. The alias exists so a route table can spell every response
 * `*ResponseSchema`, the way `library.ts` aliases `FavoriteParamsSchema`.
 */
export const PadKitResponseSchema = PadKitSchema;
/* ────────────────────────────────────────────────────────────────────────────
 * The factory kit
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * The bank as it ships, returned by `GET /api/pads/kit` for a user who has never saved
 * one. Four rows of four, top to bottom, in {@link PAD_SLOTS} order:
 *
 * ```
 *   HORN     WHISTLE  SHOT     LASER      A01-A04   the big gestures, used once a tune
 *   ORGAN    MELODICA SPRING   BRAKE      A05-A08   keys and studio effects
 *   RIM      COWBELL  TOM      OPEN       A09-A12   percussion
 *   ONE DROP SNARE    HAT      BASS       A13-A16   the riddim itself
 * ```
 *
 * The order is thumb reach, not taxonomy: on a phone held one-handed the **bottom** row
 * is the easiest to hit, so the four pads that get played rhythmically live there and the
 * once-a-tune air horn lives furthest away. Rows are also single categories, which is
 * what lets one legend teach sixteen Latin prints.
 *
 * **All sixteen ship playable.** Seven are {@link SYNTH_VOICES}, which need no file and
 * are the correct instrument for a drum machine rather than a stand-in; the other nine
 * name real CC0 recordings out of `assets/pads/` — the nine sounds synthesis either
 * cannot fake at all (horn, whistle, melodica, cross-stick) or loses badly on (the open
 * hat's six unsynchronised squares). Every id here must exist in the library, and the
 * `PUT` route validates that on the way in, so a factory slot naming a missing file is
 * caught by `pads.test.ts` rather than by a silent pad on a phone.
 *
 * An empty pad — `null` — remains a designed state: it prints its slot number, is
 * dashed, and does not sound. Nothing ships that way, but a user may clear a pad.
 *
 * `updatedAt: 0` is the "no row exists" marker. `UnixSeconds` admits it, and it makes
 * last-write-wins behave: any real save is newer, so the factory kit can never overwrite
 * one. A client must therefore treat `updatedAt === 0` as "unsaved", never as 1970.
 */
const FACTORY_SLOTS = {
    /* the big gestures — a real recording each, because synthesis cannot fake any of them */
    A01: 'air-horn', // HORN     — a pressurised horn on an Alberta oil rig
    A02: 'pea-01', // WHISTLE  — VCSL ball whistle, i.e. a pea whistle, studio cut
    A03: `${SYNTH_PREFIX}shot`, //          the lick shot is a stylised crack, not a firearm
    A04: 'siren-01', // LASER    — Roland SH-101 through a square LFO, the fastest warble
    /* keys and studio effects */
    A05: 'skank-01', // ORGAN    — the Hammond voice of the reggae skank chord toolkit
    A06: 'melo-c', // MELODICA — an actual melodica, C. The gap the licence pass called unfillable
    A07: 'spring-01', // SPRING   — a kicked spring tank
    A08: `${SYNTH_PREFIX}brake`, //          a playbackRate ramp is the honest tape stop
    /* percussion */
    A09: 'x-stick', // RIM      — a real reggae cross-stick, the sound that defines the genre
    A10: `${SYNTH_PREFIX}cowbell`, //          the 808 cowbell IS two squares at 540 and 800 Hz
    A11: `${SYNTH_PREFIX}tom`,
    A12: 'hat-opn', // OPEN     — six unsynchronised squares is the one hat synthesis loses
    /* the riddim itself, on the bottom row where a one-handed thumb reaches */
    A13: `${SYNTH_PREFIX}one-drop`,
    A14: `${SYNTH_PREFIX}snare`,
    A15: 'hat-cl', // HAT      — its pair, from the same session. Never two kits.
    A16: `${SYNTH_PREFIX}bass`, //          pitched to 80–120 Hz or a phone plays nothing
};
/**
 * The **role** printed on each pad face, and it is not the name of the sound sitting there.
 *
 * A real machine silkscreens its legend onto the panel; the sound behind it is whatever
 * you loaded. This screen does the same, and the reason is a defect that shipped: the pad
 * used to print the loaded sound's own catalogue label, so the bank read
 * `SLIDE DN · PEA 01 · SIREN 01 · SKANK 01` — internal, numbered names that are exactly
 * right in a library list of seventy-two and exactly wrong as sixteen legends you have to
 * read at arm's length in the dark. It also meant the legends changed the moment a pad
 * was reassigned, and half of them were too long for a narrow pad and got clipped.
 *
 * So: the slot's role is fixed and printed, the sound's own label lives in the library
 * sheet where telling six sirens apart is the whole job, and the Russian readout lane
 * under the plate names what actually fired. Three surfaces, three different questions.
 *
 * Roles are at most eight characters — `ONE DROP` and `MELODICA` are the longest, and
 * `MELODICA` is the one that cannot wrap, which is what sets the 64px pad floor in
 * `pads.module.css`. They match {@link FACTORY_KIT}'s rows: the big gestures, keys and
 * effects, percussion, then the riddim on the bottom row where a one-handed thumb reaches.
 */
export const PAD_ROLES = Object.freeze({
    A01: 'HORN',
    A02: 'WHISTLE',
    A03: 'SHOT',
    A04: 'SIREN',
    A05: 'ORGAN',
    A06: 'MELODICA',
    A07: 'SPRING',
    A08: 'BRAKE',
    A09: 'RIM',
    A10: 'COWBELL',
    A11: 'TOM',
    A12: 'OPEN',
    A13: 'ONE DROP',
    A14: 'SNARE',
    A15: 'HAT',
    A16: 'BASS',
});
/** The factory bank. Frozen: it is handed straight to callers and must not be edited. */
export const FACTORY_KIT = Object.freeze({
    slots: Object.freeze(FACTORY_SLOTS),
    updatedAt: 0,
});
//# sourceMappingURL=pads.js.map